/**
 * Finding Leo's view markup in a reply — and nothing more: the server parses and renders it
 * (src/operations/data_views.py). Chat panels and views are lifted out of the text before markdown
 * runs (their bodies are SQL and JSON), leaving attribute-free placeholders the renderer draws.
 *
 * Leo writes this grammar from its analytics/ region (code_mode/analytics_region.py), so matching is exact —
 * lowercase tags, a panel with a `<sql>` query, a view named `analytics` — and a reply that merely mentions a
 * `<view>` or `<panel>` (an Android layout, an SVG) renders as it always did.
 *
 * Pure functions, no React.
 */

const ATTRS = String.raw`((?:\s+[a-z_]+\s*=\s*"[^"]*")*)`;
const PANEL_BLOCK = new RegExp(String.raw`<panel\b${ATTRS}\s*>([\s\S]*?)<\/panel>`, 'g');
const VIEW_BLOCK = new RegExp(String.raw`<view\b${ATTRS}\s*>([\s\S]*?)<\/view>`, 'g');
const OPEN_BLOCK = new RegExp(String.raw`<(view|panel)\b${ATTRS}\s*>`, 'g');
const TITLE = /\btitle\s*=\s*"([^"]*)"/;
const NAME = /\bname\s*=\s*"([^"]*)"/;
const ANALYTICS = 'analytics';

export interface ChatBlock {
  markup: string;
  title: string;
}

/** The last complete `<view name="…">` in a reply — what a finished reply saves. */
export function extractView(text: string, name: string): string | null {
  let found: string | null = null;
  for (const match of (text || '').matchAll(VIEW_BLOCK)) {
    if ((match[1].match(NAME)?.[1] ?? '') === name) found = match[0];
  }
  return found;
}

/** Analytics views become `<viewcard ref="i"/>`, query panels outside them `<panel ref="i"/>`. */
export function extractBlocks(text: string): { text: string; panels: ChatBlock[]; views: string[] } {
  const views: string[] = [];
  const panels: ChatBlock[] = [];
  const withoutViews = (text || '').replace(VIEW_BLOCK, (block, attrs: string) => {
    if (attrs.match(NAME)?.[1] !== ANALYTICS) return block;
    views.push(block);
    return `<viewcard ref="${views.length - 1}"/>`;
  });
  const out = withoutViews.replace(PANEL_BLOCK, (block, attrs: string, body: string) => {
    if (!body.includes('<sql>')) return block;
    panels.push({ markup: block, title: attrs.match(TITLE)?.[1] ?? '' });
    return `<panel ref="${panels.length - 1}"/>`;
  });
  return { text: out, panels, views };
}

/** While a reply streams, hide everything from an analytics `<view …>` or a `<panel …>` that hasn't closed yet. */
export function hideOpenBlocks(text: string): string {
  const source = text || '';
  for (const match of source.matchAll(OPEN_BLOCK)) {
    const [opening, tag, attrs] = match;
    if (tag === 'view' && attrs.match(NAME)?.[1] !== ANALYTICS) continue;
    if (!source.slice((match.index ?? 0) + opening.length).includes(`</${tag}>`)) return source.slice(0, match.index);
  }
  return source;
}

const PANEL_OPEN = new RegExp(String.raw`^<panel\b${ATTRS}`);
const PLACE = /\bplace\s*=\s*"([^"]*)"/;

/** Whether a chat panel may be pinned to the Analytics dashboard: a plan's chart (`place="plan"`) stays in its plan. */
export function pinnable(markup: string): boolean {
  return !(markup || '').match(PANEL_OPEN)?.[1].match(PLACE)?.[1];
}

/** Titles of the panels a view names, kept ones included — for the "Analytics dashboard updated" card. */
export function viewPanelTitles(markup: string): string[] {
  return [...(markup || '').matchAll(/<panel\b[^>]*?\btitle\s*=\s*"([^"]*)"/gi)].map((m) => m[1]);
}
